import React, {
  useCallback,
  useEffect,
  useState,
  useContext,
  useRef,
} from "react";
import {
  NovedadesApi,
  novedadFotoPublicUrl,
} from "../../api/Novedades/NovedadesApi";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import ImageIcon from "@mui/icons-material/Image";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";

import {
  Box,
  Typography,
  TextField,
  Button,
  Snackbar,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Chip,
  useMediaQuery,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { DataGrid } from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import { SidebarContext } from "../Sidebar/SidebarProvider";
import MaterialotecaPhotoLightbox from "../Materialoteca/MaterialotecaPhotoLightbox";
import { NovedadRichTextEditor } from "./NovedadRichText";
import {
  htmlToPlainText,
  isEmptyNovedadHtml,
  novedadHtmlForDisplay,
} from "../../utils/novedadHtml";
import {
  pageHeroShellSx,
  pageContentCardSx,
  pageFieldSx,
  pageSectionTitleSx,
  pageKpiChipSx,
  pagePrimaryButtonSx,
  pageMobileCardSx,
  pageDropZoneSx,
  pageDataGridSx,
  pageVersionChipSx,
} from "../../styles/pageModernStyles";

const clickableThumbSx = {
  cursor: "zoom-in",
  "&:hover": { opacity: 0.88 },
};

const ACCEPTED_IMAGE_TYPES = /^image\/(jpeg|jpg|pjpeg|png|gif|webp)$/i;

const isAcceptableImageFile = (file) =>
  Boolean(file && ACCEPTED_IMAGE_TYPES.test(file.type || ""));

const NovedadesInsertar = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { contentMargin } = useContext(SidebarContext);
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);

  const [rows, setRows] = useState([]);
  const [editingId, setEditingId] = useState(null);

  const [deleteId, setDeleteId] = useState(null);
  const [openDelete, setOpenDelete] = useState(false);

  const [pendingFiles, setPendingFiles] = useState([]);
  const [pendingPreviews, setPendingPreviews] = useState([]);
  const [existingFotos, setExistingFotos] = useState([]);
  const [saving, setSaving] = useState(false);
  const [dropActive, setDropActive] = useState(false);
  const [photoLightbox, setPhotoLightbox] = useState(null);

  const [form, setForm] = useState({
    Version: "",
    Titulo: "",
    Descripcion: "",
    Tags: "",
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const loadData = async () => {
    const data = await NovedadesApi.getAll();
    setRows([...data].sort((a, b) => b.Id - a.Id));
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    return () => {
      pendingPreviews.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [pendingPreviews]);

  const clearPendingFiles = () => {
    pendingPreviews.forEach((url) => URL.revokeObjectURL(url));
    setPendingFiles([]);
    setPendingPreviews([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const addImageFiles = useCallback((fileList) => {
    const images = Array.from(fileList || []).filter(isAcceptableImageFile);
    if (images.length === 0) return false;

    const previews = images.map((f) => URL.createObjectURL(f));
    setPendingFiles((prev) => [...prev, ...images]);
    setPendingPreviews((prev) => [...prev, ...previews]);
    return true;
  }, []);

  const handleFileSelect = (e) => {
    addImageFiles(e.target.files);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  useEffect(() => {
    const onPaste = (e) => {
      const items = Array.from(e.clipboardData?.items || []);
      const files = items
        .filter(
          (item) =>
            item.kind === "file" && ACCEPTED_IMAGE_TYPES.test(item.type || ""),
        )
        .map((item) => item.getAsFile())
        .filter(Boolean);

      if (!files.length) return;
      e.preventDefault();
      addImageFiles(files);
    };

    document.addEventListener("paste", onPaste);
    return () => document.removeEventListener("paste", onPaste);
  }, [addImageFiles]);

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.types?.includes("Files")) setDropActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget)) setDropActive(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDropActive(false);
    addImageFiles(e.dataTransfer.files);
  };

  const removePendingAt = (index) => {
    URL.revokeObjectURL(pendingPreviews[index]);
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
    setPendingPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const handleEdit = (row) => {
    setEditingId(row.Id);
    setForm({
      Version: row.Version || "",
      Titulo: row.Titulo || "",
      Descripcion: novedadHtmlForDisplay(row.Descripcion || ""),
      Tags: row.Tags || "",
    });
    setExistingFotos(row.fotos || []);
    clearPendingFiles();
  };

  const resetForm = () => {
    setEditingId(null);
    setForm({
      Version: "",
      Titulo: "",
      Descripcion: "",
      Tags: "",
    });
    setExistingFotos([]);
    clearPendingFiles();
  };

  const handleDeleteFoto = async (fotoId) => {
    if (!editingId) return;
    try {
      await NovedadesApi.deleteFoto(editingId, fotoId);
      setExistingFotos((prev) => prev.filter((f) => f.Id !== fotoId));
      setSnackbar({
        open: true,
        message: "Foto eliminada",
        severity: "success",
      });
      loadData();
    } catch {
      setSnackbar({
        open: true,
        message: "Error eliminando foto",
        severity: "error",
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      let novedadId = editingId;
      const payload = {
        ...form,
        Descripcion: isEmptyNovedadHtml(form.Descripcion)
          ? ""
          : novedadHtmlForDisplay(form.Descripcion),
      };

      if (editingId) {
        await NovedadesApi.update(editingId, payload);
        setSnackbar({
          open: true,
          message: "Novedad actualizada",
          severity: "success",
        });
      } else {
        const created = await NovedadesApi.insertar(payload);
        novedadId = created?.Id;
        window.dispatchEvent(new Event("novedad-creada"));
        setSnackbar({
          open: true,
          message: "Novedad creada",
          severity: "success",
        });
      }

      if (novedadId && pendingFiles.length > 0) {
        await NovedadesApi.uploadFotos(novedadId, pendingFiles);
        if (!editingId) {
          window.dispatchEvent(new Event("novedad-creada"));
        }
      }

      resetForm();
      loadData();
    } catch {
      setSnackbar({
        open: true,
        message: "Error guardando novedad",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setOpenDelete(true);
  };

  const confirmDelete = async () => {
    try {
      await NovedadesApi.delete(deleteId);

      setSnackbar({
        open: true,
        message: "Novedad eliminada",
        severity: "success",
      });

      setOpenDelete(false);
      setDeleteId(null);

      if (editingId === deleteId) resetForm();
      await loadData();

      window.dispatchEvent(new Event("novedad-creada"));
    } catch {
      setSnackbar({
        open: true,
        message: "Error eliminando novedad",
        severity: "error",
      });
    }
  };

  const openPhotoLightbox = (src, alt = "") => {
    if (!src) return;
    setPhotoLightbox({ src, alt });
  };

  const renderFotoThumbs = (fotos, max = 3) => {
    if (!fotos?.length) {
      return (
        <Typography variant="caption" color="text.secondary">
          —
        </Typography>
      );
    }
    const visible = fotos.slice(0, max);
    return (
      <Stack direction="row" spacing={0.5} sx={{ mt: 1 }}>
        {visible.map((f) => (
          <Box
            key={f.Id || f.Archivo}
            component="img"
            src={novedadFotoPublicUrl(f.Archivo)}
            alt=""
            onClick={(e) => {
              e.stopPropagation();
              openPhotoLightbox(
                novedadFotoPublicUrl(f.Archivo),
                f.Archivo || "",
              );
            }}
            sx={{
              width: 40,
              height: 40,
              objectFit: "cover",
              borderRadius: 1,
              border: `1px solid ${alpha(theme.palette.divider, 0.85)}`,
              ...clickableThumbSx,
            }}
          />
        ))}
        {fotos.length > max && (
          <Typography variant="caption" sx={{ alignSelf: "center" }}>
            +{fotos.length - max}
          </Typography>
        )}
      </Stack>
    );
  };

  const columns = [
    {
      field: "Version",
      headerName: "Versión",
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          sx={{ ...pageVersionChipSx(theme), mt: 1 }}
        />
      ),
    },
    {
      field: "Titulo",
      headerName: "Título",
      width: 130,
      renderCell: (params) => (
        <Typography
          sx={{
            display: "flex",
            alignItems: "center",
            fontWeight: 800,
            mt: 1,
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: "Descripcion",
      headerName: "Descripción",
      flex: 1,
      valueGetter: (_value, row) => htmlToPlainText(row?.Descripcion),
    },
    { field: "Tags", headerName: "Tags", width: 130 },
    {
      field: "fotos",
      headerName: "Fotos",
      width: 160,
      sortable: false,
      renderCell: (params) => renderFotoThumbs(params.row.fotos),
    },
    {
      field: "actions",
      headerName: "",
      width: 110,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.25} sx={{ mt: 0.5 }}>
          <IconButton
            size="small"
            onClick={() => handleEdit(params.row)}
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.16) },
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>

          <IconButton
            size="small"
            color="error"
            onClick={() => handleDeleteClick(params.row.Id)}
            sx={{
              bgcolor: alpha(theme.palette.error.main, 0.08),
              "&:hover": { bgcolor: alpha(theme.palette.error.main, 0.16) },
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Stack>
      ),
    },
  ];

  const hasFotoPreviews =
    existingFotos.length > 0 || pendingPreviews.length > 0;

  const renderMobileRow = (row) => (
    <Box
      key={row.Id}
      sx={pageMobileCardSx(theme, { active: editingId === row.Id })}
    >
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        spacing={1}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            {row.Version ? (
              <Chip
                label={row.Version}
                size="small"
                sx={pageVersionChipSx(theme)}
              />
            ) : null}
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 800, lineHeight: 1.3 }}
              noWrap
            >
              {row.Titulo || "—"}
            </Typography>
          </Stack>
          {row.Tags ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 0.5 }}
            >
              {row.Tags}
            </Typography>
          ) : null}
          {row.Descripcion ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 0.75,
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {htmlToPlainText(row.Descripcion)}
            </Typography>
          ) : null}
          {renderFotoThumbs(row.fotos, 4)}
        </Box>
        <Stack direction="row" spacing={0.25} sx={{ flexShrink: 0 }}>
          <IconButton size="small" onClick={() => handleEdit(row)} aria-label="Editar">
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={() => handleDeleteClick(row.Id)}
            aria-label="Eliminar"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>
    </Box>
  );

  return (
    <Box
      sx={{
        ...contentMargin,
        p: { xs: 1, sm: 2 },
        minHeight: "100vh",
        boxSizing: "border-box",
        pb: { xs: 10, sm: 4 },
      }}
    >
      <Box sx={pageHeroShellSx(theme, { sticky: isMobile })}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          spacing={1.5}
          sx={{ px: { xs: 1.5, sm: 2.5 }, py: { xs: 1.5, sm: 2 } }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: alpha(theme.palette.primary.main, 0.14),
                color: "primary.main",
              }}
            >
              <CampaignOutlinedIcon />
            </Box>
            <Box>
              <Typography sx={pageSectionTitleSx}>Admin · Novedades</Typography>
              <Typography
                variant="h5"
                sx={{ fontWeight: 900, lineHeight: 1.2, fontSize: { xs: "1.15rem", sm: "1.4rem" } }}
              >
                Gestión de novedades
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                Publica versiones, changelog y material visual para el equipo.
              </Typography>
            </Box>
          </Stack>
          <Chip
            label={`${rows.length} publicadas`}
            size="small"
            color="primary"
            variant="outlined"
            sx={pageKpiChipSx}
          />
        </Stack>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", lg: "row" },
          gap: { xs: 2, lg: 2.5 },
          mt: 1.5,
        }}
      >
      <Box
        sx={{
          ...pageContentCardSx(theme, theme.palette.primary.main, {
            highlighted: Boolean(editingId),
          }),
          width: { xs: "100%", lg: "30%" },
          flexShrink: 0,
        }}
      >
        <Typography
          variant="overline"
          sx={{ ...pageSectionTitleSx, display: "block", mb: 0.5 }}
        >
          {editingId ? "Editar" : "Nueva"}
        </Typography>
        <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 800, fontSize: { xs: "1rem", sm: "1.15rem" } }}>
          {editingId ? "Editar novedad" : "Nueva novedad"}
        </Typography>
        <form onSubmit={handleSubmit}>
          <TextField
            label="Versión"
            name="Version"
            value={form.Version}
            onChange={handleChange}
            fullWidth
            margin="normal"
            size="small"
            sx={pageFieldSx(theme)}
          />
          <TextField
            label="Título"
            name="Titulo"
            value={form.Titulo}
            onChange={handleChange}
            fullWidth
            margin="normal"
            size="small"
            sx={pageFieldSx(theme)}
          />
          <NovedadRichTextEditor
            key={editingId || "new"}
            value={form.Descripcion}
            onChange={(html) =>
              setForm((prev) => ({ ...prev, Descripcion: html }))
            }
          />
          <TextField
            label="Tags"
            name="Tags"
            value={form.Tags}
            onChange={handleChange}
            fullWidth
            margin="normal"
            size="small"
            sx={pageFieldSx(theme)}
          />

          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={pageSectionTitleSx} gutterBottom>
              Fotos
            </Typography>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              multiple
              hidden
              onChange={handleFileSelect}
            />
            <Box
              ref={dropZoneRef}
              tabIndex={0}
              role="button"
              aria-label="Zona para subir imágenes: arrastrar, pegar o hacer clic"
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={(e) => {
                if (e.target.closest("button")) return;
                fileInputRef.current?.click();
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  fileInputRef.current?.click();
                }
              }}
              sx={{
                ...pageDropZoneSx(theme, { active: dropActive }),
                p: hasFotoPreviews ? 1.5 : 2.5,
              }}
            >
              {!hasFotoPreviews && (
                <Stack alignItems="center" spacing={0.5} sx={{ py: 1 }}>
                  <ImageIcon sx={{ fontSize: 36, color: "text.secondary" }} />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    textAlign="center"
                  >
                    Arrastra imágenes aquí
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    textAlign="center"
                  >
                    o haz clic · pega con Ctrl+V
                  </Typography>
                </Stack>
              )}

              {hasFotoPreviews && (
                <Stack
                  direction="row"
                  flexWrap="wrap"
                  gap={1}
                  onClick={(e) => e.stopPropagation()}
                >
                  {existingFotos.map((f) => (
                    <Box key={f.Id} sx={{ position: "relative" }}>
                      <Box
                        component="img"
                        src={novedadFotoPublicUrl(f.Archivo)}
                        alt=""
                        onClick={(e) => {
                          e.stopPropagation();
                          openPhotoLightbox(
                            novedadFotoPublicUrl(f.Archivo),
                            f.Archivo || "",
                          );
                        }}
                        sx={{
                          width: 72,
                          height: 72,
                          objectFit: "cover",
                          borderRadius: 1.5,
                          border: `1px solid ${alpha(theme.palette.divider, 0.85)}`,
                          ...clickableThumbSx,
                        }}
                      />
                      {editingId && (
                        <IconButton
                          size="small"
                          color="error"
                          sx={{
                            position: "absolute",
                            top: -8,
                            right: -8,
                            bgcolor: "background.paper",
                            boxShadow: 1,
                            "&:hover": { bgcolor: alpha(theme.palette.error.main, 0.12) },
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteFoto(f.Id);
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  ))}
                  {pendingPreviews.map((url, i) => (
                    <Box key={url} sx={{ position: "relative" }}>
                      <Box
                        component="img"
                        src={url}
                        alt=""
                        onClick={(e) => {
                          e.stopPropagation();
                          openPhotoLightbox(url, "Vista previa");
                        }}
                        sx={{
                          width: 72,
                          height: 72,
                          objectFit: "cover",
                          borderRadius: 1.5,
                          border: `1px dashed ${theme.palette.primary.main}`,
                          ...clickableThumbSx,
                        }}
                      />
                      <IconButton
                        size="small"
                        color="error"
                        sx={{
                          position: "absolute",
                          top: -8,
                          right: -8,
                          bgcolor: "background.paper",
                          boxShadow: 1,
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          removePendingAt(i);
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Stack>
              )}

              {hasFotoPreviews && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  textAlign="center"
                  sx={{ mt: 1 }}
                >
                  Arrastra más · pega (Ctrl+V) · clic para explorar
                </Typography>
              )}
            </Box>
          </Box>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            sx={{ mt: 2 }}
          >
            <Button
              type="submit"
              variant="contained"
              disabled={saving}
              fullWidth
              sx={{ flex: 1, ...pagePrimaryButtonSx(theme) }}
            >
              {editingId ? "Actualizar" : "Crear"}
            </Button>

            {editingId ? (
              <Button
                onClick={resetForm}
                fullWidth={isMobile}
                sx={{ flexShrink: 0, borderRadius: 2, textTransform: "none", fontWeight: 700 }}
              >
                Cancelar
              </Button>
            ) : null}
          </Stack>
        </form>
      </Box>
      <Box
        sx={{
          ...pageContentCardSx(theme, theme.palette.info?.main || theme.palette.secondary.main),
          width: { xs: "100%", lg: "70%" },
          minWidth: 0,
          flex: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Typography variant="overline" sx={{ ...pageSectionTitleSx, display: "block", mb: 0.5 }}>
          Listado
        </Typography>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 800, fontSize: { xs: "1rem", sm: "1.15rem" }, mb: 1.5 }}>
          Novedades publicadas
        </Typography>

        {isMobile ? (
          <Stack spacing={1.25} sx={{ flex: 1 }}>
            {!rows.length ? (
              <Box
                sx={{
                  py: 4,
                  textAlign: "center",
                  borderRadius: 2,
                  border: "1px dashed",
                  borderColor: alpha(theme.palette.divider, 0.8),
                  bgcolor: alpha(theme.palette.background.default, 0.5),
                }}
              >
                <Typography color="text.secondary">No hay novedades todavía</Typography>
              </Box>
            ) : (
              rows.map(renderMobileRow)
            )}
          </Stack>
        ) : (
          <Box
            sx={{
              flex: 1,
              minHeight: 400,
              height: { md: "calc(100vh - 280px)" },
              width: "100%",
              minWidth: 0,
            }}
          >
            <DataGrid
              rows={rows}
              columns={columns}
              getRowId={(row) => row.Id}
              disableRowSelectionOnClick
              sx={pageDataGridSx(theme)}
            />
          </Box>
        )}
      </Box>
      </Box>

      <Dialog
        open={openDelete}
        onClose={() => setOpenDelete(false)}
        fullScreen={isMobile}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 3,
            border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
          },
        }}
      >
        <DialogTitle>Confirmar eliminación</DialogTitle>
        <DialogContent>
          <Typography>¿Eliminar esta novedad y sus fotos?</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setOpenDelete(false)} sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700 }}>
            Cancelar
          </Button>
          <Button
            onClick={confirmDelete}
            color="error"
            variant="contained"
            sx={{ borderRadius: 2, textTransform: "none", fontWeight: 800, boxShadow: `0 4px 14px ${alpha(theme.palette.error.main, 0.3)}` }}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>

      <MaterialotecaPhotoLightbox
        open={Boolean(photoLightbox)}
        src={photoLightbox?.src}
        alt={photoLightbox?.alt || ""}
        onClose={() => setPhotoLightbox(null)}
        closeAriaLabel="Cerrar imagen"
      />
    </Box>
  );
};

export default NovedadesInsertar;
